import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PresenceChannel } from 'pusher-js';
import * as Sentry from '@sentry/react';
import { usePusher } from '@contexts/PusherContext';

export type PresenceMemberType = 'operator' | 'patient' | 'unknown';

export interface PresenceMember {
  id: string;
  userType: PresenceMemberType;
}

export interface MessageEvent {
  action: 'created' | 'updated' | 'destroyed';
  messageId: string;
  conversationId: string;
  direction: 'incoming' | 'outgoing';
  createdAt: string;
}

interface UseConversationPresenceOptions {
  /** Callback when a new message is created */
  onMessageEvent?: (event: MessageEvent) => void;
}

interface UseConversationPresenceReturn {
  /** Whether any staff member is currently viewing the conversation */
  isStaffViewing: boolean;
  /** Whether any staff member is currently typing */
  isStaffTyping: boolean;
  /** Number of staff members viewing */
  staffViewingCount: number;
  /** Send typing indicator */
  setTyping: (isTyping: boolean) => void;
  /** Whether successfully subscribed to presence channel */
  isSubscribed: boolean;
}

/**
 * Parse presence member ID to extract userType and actual ID
 * Supports both new format (operator_uuid) and legacy format (uuid only)
 */
function parsePresenceId(presenceId: string): { userType: PresenceMemberType; id: string } {
  const underscoreIndex = presenceId.indexOf('_');
  if (underscoreIndex === -1) {
    // Legacy format: bare UUID, assume operator for backward compatibility
    return { userType: 'operator', id: presenceId };
  }
  const prefix = presenceId.slice(0, underscoreIndex);
  const id = presenceId.slice(underscoreIndex + 1);
  if (prefix === 'operator' || prefix === 'patient') {
    return { userType: prefix, id };
  }
  // Unknown prefix, treat as unknown type but keep original ID
  return { userType: 'unknown', id: presenceId };
}

export function useConversationPresence(
  conversationId: string | undefined,
  options?: UseConversationPresenceOptions
): UseConversationPresenceReturn {
  const { pusher } = usePusher();
  const [members, setMembers] = useState<PresenceMember[]>([]);
  const [typingIds, setTypingIds] = useState<Set<string>>(new Set());
  const [isSubscribed, setIsSubscribed] = useState(false);
  const channelRef = useRef<PresenceChannel | null>(null);
  const onMessageEventRef = useRef(options?.onMessageEvent);

  // Keep callback ref up to date
  useEffect(() => {
    onMessageEventRef.current = options?.onMessageEvent;
  }, [options?.onMessageEvent]);

  useEffect(() => {
    if (!conversationId || !pusher) {
      if (import.meta.env.DEV) {
        console.log('[Presence] Skipping subscription:', { conversationId, hasPusher: !!pusher });
      }
      return;
    }

    // Reset state for fresh subscription
    setIsSubscribed(false);
    setTypingIds(new Set());

    const channelName = `presence-conversation-${conversationId}`;
    if (import.meta.env.DEV) {
      console.log('[Presence] Subscribing to:', channelName);
    }
    const channel = pusher.subscribe(channelName) as PresenceChannel;
    channelRef.current = channel;

    const updateMembers = () => {
      const memberList: PresenceMember[] = [];
      channel.members.each((member: { id: string; info: Record<string, unknown> }) => {
        // Exclude self from the list
        if (member.id !== channel.members.me?.id) {
          const parsed = parsePresenceId(member.id);
          memberList.push({
            id: parsed.id,
            userType: parsed.userType,
          });
        }
      });
      setMembers(memberList);
    };

    const handleMemberRemoved = (member: { id: string }) => {
      // Remove from typing set when member leaves
      setTypingIds((prev) => {
        const next = new Set(prev);
        next.delete(member.id);
        return next;
      });
      updateMembers();
    };

    channel.bind('pusher:subscription_succeeded', () => {
      if (import.meta.env.DEV) {
        console.log('[Presence] Subscription succeeded:', channelName);
      }
      setIsSubscribed(true);
      updateMembers();
    });
    channel.bind('pusher:member_added', updateMembers);
    channel.bind('pusher:member_removed', handleMemberRemoved);

    // Error handling for subscription failures
    channel.bind('pusher:subscription_error', (status: unknown) => {
      if (import.meta.env.DEV) {
        console.error('[Presence] Subscription failed:', status);
        return;
      }
      // The presence auth request (/patients/websocket) failing is almost always
      // transient churn on mobile Safari rather than an actionable bug:
      //   • status 0  — no response at all (network drop, tab frozen/backgrounded, nav away)
      //   • 401 / 403 — the patient's session had lapsed; the auth/heartbeat flow recovers
      //   • other 4xx — timeout / rate limit
      // Only a genuine server-side failure (5xx) — or an unexpected non-numeric status —
      // is worth surfacing to Sentry.
      const authStatus =
        typeof status === 'object' && status !== null
          ? (status as { status?: number }).status
          : undefined;
      if (typeof authStatus === 'number' && authStatus < 500) {
        return;
      }
      Sentry.captureException(new Error('Presence subscription failed'), {
        tags: { pusher: true },
        extra: { channelName, status },
      });
    });

    // Typing events - only process if not from self
    channel.bind('client-typing-start', (data: { userId: string }) => {
      if (data.userId === channel.members.me?.id) return;
      setTypingIds((prev) => new Set(prev).add(data.userId));
    });

    channel.bind('client-typing-stop', (data: { userId: string }) => {
      if (data.userId === channel.members.me?.id) return;
      setTypingIds((prev) => {
        const next = new Set(prev);
        next.delete(data.userId);
        return next;
      });
    });

    // Message events from server
    channel.bind('message-event', (data: MessageEvent) => {
      onMessageEventRef.current?.(data);
    });

    return () => {
      channel.unbind_all();
      pusher.unsubscribe(channelName);
      channelRef.current = null;
      setIsSubscribed(false);
    };
  }, [conversationId, pusher]);

  const setTyping = useCallback((isTyping: boolean) => {
    const channel = channelRef.current;
    if (!channel || !channel.members.me) return;

    const eventName = isTyping ? 'client-typing-start' : 'client-typing-stop';
    channel.trigger(eventName, { userId: channel.members.me.id });
  }, []);

  // Derived values for staff presence
  const staffMembers = useMemo(
    () => members.filter((m) => m.userType === 'operator'),
    [members]
  );

  const isStaffViewing = staffMembers.length > 0;
  const staffViewingCount = staffMembers.length;

  const isStaffTyping = useMemo(() => {
    return staffMembers.some((m) => {
      // Check both formats in typing set
      const presenceId = `operator_${m.id}`;
      return typingIds.has(presenceId) || typingIds.has(m.id);
    });
  }, [staffMembers, typingIds]);

  return {
    isStaffViewing,
    isStaffTyping,
    staffViewingCount,
    setTyping,
    isSubscribed,
  };
}
